/**
 * The handoff: a Flow answer that needs the real form (FLOW_SCREEN_SPEC.md §2.8).
 *
 * Karr has one invoice form, one bill form and one expense form. "I sold
 * something" asks Who in the sheet, then hands the person to the real invoice
 * form with the customer already set, and takes them back to the sheet's Done
 * card when it posts. The wizard's old mini forms are retired as forms; there
 * is no second way to raise an invoice that can drift from the first.
 *
 * The contract, in order:
 *
 *   1. `startHandoff` takes back the sheet's history entries FIRST (the flow-2
 *      spike: after a real navigation, Back lands on a dead flow entry), then
 *      navigates to `/sales?new=invoice&contact=<id>&from=record`.
 *   2. The page reads `new`, `contact` and `from` once its contacts have
 *      loaded (`readHandoffParams`), opens its form with the customer set, and
 *      strips the words from the address (`stripHandoffParams`), so a reload
 *      does not open a second form.
 *   3. The form's header shows the trail ("Part of: I sold something ›
 *      Sharma Traders"); its money question posts as the operation's steps.
 *   4. On post the page calls `completeHandoff`, and the layout re-expands the
 *      sheet on the Done card, read back from what posted. On cancel,
 *      `cancelHandoff`, and the sheet opens again at Who.
 *
 * The session lives here, not in the sheet: every page draws its own layout,
 * so the sheet that started the handoff is gone by the time the invoice
 * posts. The state is mirrored into sessionStorage so a reload on the form
 * still finishes the flow, and it is dropped after six hours.
 *
 * "Minimised" (the spec's word) is this: while the person is on the form, the
 * sheet is closed and its session waits here; the trail in the form's header
 * is the only reminder, because a second orientation bar is exactly what the
 * founder rejected on the desk.
 *
 * @since flow-3 (24 Sep 2026)
 */
import { useSyncExternalStore } from 'react';

export type HandoffKind = 'invoice' | 'bill' | 'expense';

export interface Handoff {
    kind: HandoffKind;
    orgId: string;
    /** The wizard answer this came from ('sale.invoice'). */
    subPathId: string;
    pathId: string | null;
    /** Null for "a small shop, no bill". */
    contactId: string | null;
    contactName: string | null;
    /** The answers so far, as the sheet said them. */
    trail: string[];
    /** The operation the document and its money post as (flow-2c). */
    operationId: string;
    /** Where the person started, for the way back. */
    returnTo: string;
    /** "A walk-in, no name": the money question starts at All, in cash. */
    walkIn?: boolean;
    startedAt: number;
}

export interface HandoffResult {
    /** The document the form posted. */
    id: string;
    number: string | null;
    /** Rupees. */
    total: number;
    paid: 'none' | 'part' | 'full';
    /** Rupees the receipt or payment recorded (0 when it failed). */
    paidAmount: number;
    /** The receipt or payment transaction, when one posted. */
    paymentId?: string | null;
    /** The money step failed: the document stands, and the home offers Finish. */
    paymentError?: string | null;
    /** The e-invoice (IRN) is not done yet. */
    einvoicePending?: boolean;
}

export type HandoffState =
    | { stage: 'away'; handoff: Handoff }
    | { stage: 'done'; handoff: Handoff; result: HandoffResult }
    | { stage: 'cancelled'; handoff: Handoff };

const KEY = 'karr.flow.handoff.v1';
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

let state: HandoffState | null = null;
let loaded = false;
const listeners = new Set<() => void>();

function persist(): void {
    try {
        if (state) sessionStorage.setItem(KEY, JSON.stringify(state));
        else sessionStorage.removeItem(KEY);
    } catch { /* private window: the in-memory copy still works */ }
}

function load(): void {
    if (loaded) return;
    loaded = true;
    try {
        const raw = sessionStorage.getItem(KEY);
        if (!raw) return;
        const s = JSON.parse(raw) as HandoffState;
        if (s?.handoff && Date.now() - s.handoff.startedAt < MAX_AGE_MS) state = s;
        else sessionStorage.removeItem(KEY);
    } catch { /* unreadable: start clean */ }
}

function set(next: HandoffState | null): void {
    state = next;
    persist();
    for (const fn of listeners) fn();
}

export function getHandoffState(): HandoffState | null {
    load();
    return state;
}

export function subscribeHandoff(fn: () => void): () => void {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
}

/** The handoff state, re-rendering when it changes. */
export function useHandoffState(): HandoffState | null {
    return useSyncExternalStore(subscribeHandoff, getHandoffState, () => null);
}

/** Tests only. */
export function resetHandoffForTests(): void {
    state = null;
    loaded = true;
    try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
}

/** The form's address for a handoff. */
export function handoffUrl(h: Pick<Handoff, 'kind' | 'contactId' | 'walkIn'>): string {
    const q = new URLSearchParams();
    if (h.kind === 'invoice') {
        q.set('new', 'invoice');
        if (h.contactId) q.set('contact', h.contactId);
        if (h.walkIn) q.set('walkin', '1');
        q.set('from', 'record');
        return `/sales?${q.toString()}`;
    }
    if (h.kind === 'bill') {
        q.set('new', 'bill');
        if (h.contactId) q.set('contact', h.contactId);
        q.set('from', 'record');
        return `/purchases?${q.toString()}`;
    }
    q.set('new', 'expense');
    if (h.contactId) q.set('vendor', h.contactId);
    q.set('from', 'record');
    return `/expenses?${q.toString()}`;
}

export interface HandoffParams {
    contactId: string | null;
    /** Came from the Flow sheet: the Done card waits for this post. */
    fromRecord: boolean;
    walkIn: boolean;
    /** A supplementary bill to this one (the supplier raised the price after billing). */
    supplementOf: string | null;
    /** flow-7: a stage of this sales order (milestone billing), and its share in %. */
    orderId?: string | null;
    share?: number | null;
    /** flow-8 (§6.0): a document from another door (a GSTR-2B row), with its facts. */
    doc?: { door: 'gstr2b' | 'network' | 'scan'; sourceId: string; gstin: string | null; number: string; date: string | null; taxable: number | null } | null;
    /** far-6b: one line, prefilled (an asset moved between two GSTINs is a supply that needs its invoice). */
    line?: { description: string; amount: number; taxRate: number | null } | null;
}

/**
 * The words a page reads from its address. `expected` is the document THIS
 * page makes (`new=invoice` is ignored by the proforma page, which embeds the
 * same component with its own kind). Null when the address asks for nothing.
 */
export function readHandoffParams(search: string, expected: HandoffKind | 'proforma'): HandoffParams | null {
    const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    if (q.get('new') !== expected) return null;
    return {
        contactId: q.get('contact') || q.get('vendor') || null,
        fromRecord: q.get('from') === 'record',
        walkIn: q.get('walkin') === '1',
        supplementOf: q.get('supplement') || null,
        // flow-7: only a stage of an order carries these.
        ...(q.get('order') ? { orderId: q.get('order'), share: q.get('share') ? Number(q.get('share')) : null } : {}),
        // flow-8: only a door's document carries these.
        ...(q.get('docsrc') && q.get('docno') ? { doc: {
            door: (q.get('door') as 'gstr2b' | 'network' | 'scan' | null) ?? 'gstr2b', sourceId: q.get('docsrc')!,
            gstin: q.get('gstin'), number: q.get('docno')!, date: q.get('docdate'),
            taxable: q.get('taxable') ? Number(q.get('taxable')) : null,
        } } : {}),
        // far-6b: only a move of an asset between two GSTINs carries these.
        ...(q.get('ldesc') && q.get('lamt') ? { line: {
            description: q.get('ldesc')!, amount: Number(q.get('lamt')), taxRate: q.get('lrate') ? Number(q.get('lrate')) : null,
        } } : {}),
    };
}

const HANDOFF_WORDS = ['new', 'contact', 'vendor', 'from', 'walkin', 'supplement', 'order', 'share', 'door', 'docsrc', 'gstin', 'docno', 'docdate', 'taxable', 'ldesc', 'lamt', 'lrate'];

/** Take the handoff's words off the address, keeping the router's own state. */
export function stripHandoffParams(win: Window = window): void {
    const url = new URL(win.location.href);
    let changed = false;
    for (const w of HANDOFF_WORDS) {
        if (url.searchParams.has(w)) { url.searchParams.delete(w); changed = true; }
    }
    if (changed) win.history.replaceState(win.history.state, '', url.toString());
}

/**
 * Hand the person to the real form. `unwind` takes the sheet's history
 * entries back and closes it; only then does `navigate` move, so Back from
 * the form lands on the page the flow started from, never on a dead step.
 */
export async function startHandoff(
    h: Omit<Handoff, 'startedAt'>,
    deps: { unwind: () => Promise<void>; navigate: (url: string) => void },
): Promise<void> {
    set({ stage: 'away', handoff: { ...h, startedAt: Date.now() } });
    await deps.unwind();
    deps.navigate(handoffUrl(h));
}

/**
 * The handoff this page should finish, if the person came from the sheet:
 * the same kind, the same business, still away.
 */
export function activeHandoff(kind: HandoffKind, orgId: string | null | undefined): Handoff | null {
    const s = getHandoffState();
    if (!s || s.stage !== 'away' || s.handoff.kind !== kind || s.handoff.orgId !== orgId) return null;
    return s.handoff;
}

/** The form posted: the sheet opens again on the Done card. */
export function completeHandoff(result: HandoffResult): void {
    const s = getHandoffState();
    if (!s || s.stage !== 'away') return;
    set({ stage: 'done', handoff: s.handoff, result });
}

/** The form was closed without posting: the sheet opens again at Who. */
export function cancelHandoff(): void {
    const s = getHandoffState();
    if (!s || s.stage !== 'away') return;
    set({ stage: 'cancelled', handoff: s.handoff });
}

/** The sheet has taken the result (or the person went elsewhere). */
export function clearHandoff(): void {
    if (getHandoffState()) set(null);
}

/** "Part of: I sold something › Sharma Traders", for the form's header. */
export function trailWords(h: Pick<Handoff, 'trail'>): string {
    return `Part of: ${h.trail.filter(Boolean).join(' › ')}`;
}

/** The operation's step names for a handoff's document and its money. */
export const HANDOFF_STEPS: Record<HandoffKind, [string, string]> = {
    invoice: ['invoice', 'receipt'],
    bill: ['bill', 'payment'],
    expense: ['expense', 'payment'],
};
